import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  url,
  is_superuser,
  is_shop_admin,
  is_shop_manager,
  franchise,
  branch,
  works_in_franchise,
  is_back_office,
} from "../../../Functionalities/Config";
import {
  getToken,
  showMessage,
  formatTime,
} from "../../../Functionalities/Functions";
import ToggleButton from "../../../CoreComponents/ToggleButton";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";

function RestaurantDeliverySlots({ branchId }) {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    id: "",
    label: "",
    from_time: "",
    to_time: "",
    max_orders: 0,
    is_active: true,
  });
  const [franchiseId, setFranchiseId] = useState(null);
  const [errors, setErrors] = useState({});
  const [franchiseList, setFranchiseList] = useState([]);

  const isFranchiseScoped =
    is_back_office;
  const ownBranchId =
    !isFranchiseScoped &&
    (is_shop_admin || is_shop_manager) &&
    Array.isArray(branch) &&
    branch.length > 0
      ? branch[0].id
      : null;
  // Backend ClsDeliverySlotConfigAdmin only authorizes superuser/franchise users;
  // shop-level roles get a read-only view via the public slots endpoint.
  const canManage = isFranchiseScoped;

  useEffect(() => {
    if (branchId) {
      const fetchBranchFranchise = async () => {
        try {
          const response = await axios.get(
            `${url}/branch/details/?id=${branchId}`,
            {
              headers: { Authorization: getToken() },
            },
          );
          if (
            response.data &&
            response.data.results &&
            response.data.results.length > 0
          ) {
            const branchObj = response.data.results[0];
            const fid =
              branchObj.franchise && typeof branchObj.franchise === "object"
                ? branchObj.franchise.id
                : branchObj.franchise;
            setFranchiseId(fid);
          }
        } catch (error) {
          console.error("Failed to fetch branch franchise:", error);
        }
      };
      fetchBranchFranchise();
    } else {
      if (works_in_franchise) {
        setFranchiseId(franchise.id);
      }
    }
  }, [branchId]);

  useEffect(() => {
    if (is_superuser && !branchId) {
      axios
        .get(
          `${url}/franchise/details/?franchise_dropdown=true&page_wise=false&active_franchise=true`,
          {
            headers: { Authorization: getToken() },
          },
        )
        .then((res) => {
          if (res.data) {
            // page_wise=false returns a bare array, so there is no `results`
            // envelope to read -- this dropdown was always empty.
            setFranchiseList(
              Array.isArray(res.data) ? res.data : res.data.results || [],
            );
          }
        })
        .catch((err) => console.error("Failed to load franchises:", err));
    }
  }, [branchId]);

  useEffect(() => {
    if (franchiseId || ownBranchId) {
      fetchSlots();
    }
  }, [franchiseId, ownBranchId]);

  const fetchSlots = async () => {
    if (!franchiseId && !ownBranchId) return;
    setLoading(true);
    try {
      if (franchiseId) {
        const response = await axios.get(
          `${url}/order/delivery-slots/admin/?franchise=${franchiseId}`,
          {
            headers: { Authorization: getToken() },
          },
        );
        if (response.data?.Status) {
          setSlots(
            Array.isArray(response.data.data) ? response.data.data : [],
          );
        } else {
          setSlots([]);
          showMessage(
            response.data?.Error || "Failed to fetch delivery slots",
            "danger",
            "Error!",
            "top",
            "top-right",
            3000,
          );
        }
      } else {
        const response = await axios.get(
          `${url}/order/delivery-slots/?branch=${ownBranchId}`,
          {
            headers: { Authorization: getToken() },
          },
        );
        setSlots(Array.isArray(response.data) ? response.data : []);
      }
    } catch (error) {
      showMessage(
        "Failed to fetch delivery slots",
        "danger",
        "Error!",
        "top",
        "top-right",
        3000,
      );
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (slot) => {
    try {
      const response = await axios.put(
        `${url}/order/delivery-slots/admin/`,
        {
          id: slot.id,
          franchise: parseInt(franchiseId),
          label: slot.label,
          from_time: slot.from_time,
          to_time: slot.to_time,
          max_orders: slot.max_orders,
          is_active: !slot.is_active,
        },
        {
          headers: {
            Authorization: getToken(),
            "Content-Type": "application/json",
          },
        },
      );
      if (response.data?.Status) {
        showMessage(
          `Slot ${!slot.is_active ? "activated" : "deactivated"} successfully`,
          "success",
          "Success!",
          "top",
          "top-right",
          2000,
        );
        fetchSlots();
      } else {
        showMessage(
          response.data?.Error || "Failed to update slot status",
          "danger",
          "Error!",
          "top",
          "top-right",
          3000,
        );
      }
    } catch (error) {
      showMessage(
        error.response?.data?.Error || "Failed to update slot status",
        "danger",
        "Error!",
        "top",
        "top-right",
        3000,
      );
    }
  };

  const openAddModal = () => {
    setEditMode(false);
    setFormData({
      id: "",
      label: "",
      from_time: "",
      to_time: "",
      max_orders: 0,
      is_active: true,
    });
    setErrors({});
    setIsModalOpen(true);
  };

  const openEditModal = (slot) => {
    setEditMode(true);
    // Strip trailing seconds if any for the HTML5 time inputs
    const stripSeconds = (timeStr) => {
      if (!timeStr) return "";
      const parts = timeStr.split(":");
      if (parts.length >= 2) {
        return `${parts[0]}:${parts[1]}`;
      }
      return timeStr;
    };

    setFormData({
      id: slot.id,
      label: slot.label,
      from_time: stripSeconds(slot.from_time),
      to_time: stripSeconds(slot.to_time),
      max_orders: slot.max_orders,
      is_active: slot.is_active,
    });
    setErrors({});
    setIsModalOpen(true);
  };

  const formatTimeWithSeconds = (timeStr) => {
    if (!timeStr) return "";
    const parts = timeStr.split(":");
    if (parts.length === 2) {
      return `${timeStr}:00`;
    }
    return timeStr;
  };

  const validateForm = () => {
    const tempErrors = {};
    if (!formData.label.trim()) tempErrors.label = "Slot label is required";
    if (!formData.from_time) tempErrors.from_time = "Start time is required";
    if (!formData.to_time) tempErrors.to_time = "End time is required";

    if (formData.from_time && formData.to_time) {
      const from = formatTimeWithSeconds(formData.from_time);
      const to = formatTimeWithSeconds(formData.to_time);
      if (from >= to) {
        tempErrors.to_time = "End time must be after start time";
      }
    }

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const payload = {
      franchise: parseInt(franchiseId),
      label: formData.label.trim(),
      from_time: formatTimeWithSeconds(formData.from_time),
      to_time: formatTimeWithSeconds(formData.to_time),
      is_active: formData.is_active,
      max_orders: parseInt(formData.max_orders) || 0,
    };

    try {
      if (editMode) {
        const response = await axios.put(
          `${url}/order/delivery-slots/admin/`,
          { ...payload, id: formData.id },
          {
            headers: {
              Authorization: getToken(),
              "Content-Type": "application/json",
            },
          },
        );
        if (response.data?.Status) {
          showMessage(
            "Delivery slot updated successfully",
            "success",
            "Success!",
            "top",
            "top-right",
            2000,
          );
          setIsModalOpen(false);
          fetchSlots();
        } else {
          showMessage(
            response.data?.Error || "Failed to save delivery slot",
            "danger",
            "Error!",
            "top",
            "top-right",
            3000,
          );
        }
      } else {
        const response = await axios.post(
          `${url}/order/delivery-slots/admin/`,
          payload,
          {
            headers: {
              Authorization: getToken(),
              "Content-Type": "application/json",
            },
          },
        );
        if (response.data?.Status) {
          showMessage(
            "Delivery slot created successfully",
            "success",
            "Success!",
            "top",
            "top-right",
            2000,
          );
          setIsModalOpen(false);
          fetchSlots();
        } else {
          showMessage(
            response.data?.Error || "Failed to save delivery slot",
            "danger",
            "Error!",
            "top",
            "top-right",
            3000,
          );
        }
      }
    } catch (error) {
      const errMsg =
        error.response?.data?.Error || "Failed to save delivery slot";
      showMessage(errMsg, "danger", "Error!", "top", "top-right", 3000);
    }
  };

  const confirmDelete = (slot) => {
    confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <div
            className="custom-ui"
            style={{
              borderRadius: 8,
              border: "solid",
              padding: "40px",
              borderWidth: 0.5,
              borderColor: "#eee",
              backgroundColor: "white",
              boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
            }}
          >
            <h3 style={{ color: "#333", fontWeight: "600" }}>Are you sure?</h3>
            <p style={{ color: "#666" }}>
              Do you want to delete delivery slot "{slot.label}"?
            </p>
            <div style={{ marginTop: "20px", display: "flex", gap: "10px" }}>
              <button
                onClick={onClose}
                className="btn btn-light"
                style={{ padding: "8px 20px", borderRadius: "6px" }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  onClose();
                  handleDelete(slot.id);
                }}
                className="btn btn-danger"
                style={{ padding: "8px 20px", borderRadius: "6px" }}
              >
                Delete
              </button>
            </div>
          </div>
        );
      },
    });
  };

  const handleDelete = async (slotId) => {
    try {
      const response = await axios.delete(
        `${url}/order/delivery-slots/admin/?id=${slotId}`,
        {
          headers: {
            Authorization: getToken(),
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.data?.Status) {
        showMessage(
          response.data?.Error || "Failed to delete slot",
          "danger",
          "Error!",
          "top",
          "top-right",
          3000,
        );
        return;
      }

      if (response.data.deleted) {
        showMessage(
          "Delivery slot deleted successfully",
          "success",
          "Success!",
          "top",
          "top-right",
          3000,
        );
      } else {
        showMessage(
          "This slot has existing customer bookings and was set to inactive instead of deleted.",
          "warning",
          "Soft-Disabled",
          "top",
          "top-right",
          6000,
        );
      }
      fetchSlots();
    } catch (error) {
      const errMsg = error.response?.data?.Error || "Failed to delete slot";
      showMessage(errMsg, "danger", "Error!", "top", "top-right", 3000);
    }
  };

  return (
    <div
      style={{
        backgroundColor: "white",
        borderRadius: 20,
        marginBottom: 30,
        marginTop: 50,
      }}
    >
      <div className="card-header d-sm-flex flex-wrap d-block pb-0 border-0">
        <div className="mr-auto pr-3">
          <h4 className="text-black fs-20" style={{ fontWeight: "600" }}>
            Delivery Time Slots
          </h4>
        </div>
        {is_superuser && !branchId && (
          <div className="mr-3 mb-2" style={{ minWidth: 200 }}>
            <select
              className="form-control"
              value={franchiseId || ""}
              onChange={(e) => setFranchiseId(e.target.value)}
              style={{ borderRadius: 10 }}
            >
              <option value="">Select Franchise</option>
              {franchiseList.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          </div>
        )}
        {canManage && franchiseId && (
          <button
            type="button"
            className="btn btn-primary btn-sm mb-2"
            onClick={openAddModal}
            style={{ borderRadius: 10, padding: "8px 16px", fontWeight: "500" }}
          >
            <i className="fa fa-plus" /> Add Slot
          </button>
        )}
      </div>

      <div className="card-body pt-2">
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        ) : !franchiseId && !ownBranchId ? (
          <div className="text-center py-5">
            <i
              className="fa fa-clock-o fs-30 mb-2"
              style={{ color: "#ccc", fontSize: "36px" }}
            />
            <p className="mb-0 text-muted">
              {is_superuser
                ? "Select a franchise to view its delivery slots."
                : "No franchise or branch is linked to your account, so delivery slots cannot be shown."}
            </p>
          </div>
        ) : slots.length === 0 ? (
          <div className="text-center py-5">
            <i
              className="fa fa-clock-o fs-30 mb-2"
              style={{ color: "#ccc", fontSize: "36px" }}
            />
            <p className="mb-0 text-muted">
              No delivery slots configured for this branch.
            </p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-responsive-md mb-0">
              <thead>
                <tr>
                  <th style={{ fontWeight: "600" }}>SLOT ID</th>
                  <th style={{ fontWeight: "600" }}>SLOT LABEL</th>
                  <th style={{ fontWeight: "600" }}>START TIME</th>
                  <th style={{ fontWeight: "600" }}>END TIME</th>
                  <th style={{ fontWeight: "600" }}>CAPACITY</th>
                  <th style={{ fontWeight: "600" }}>STATUS</th>
                  {canManage && <th style={{ fontWeight: "600" }}>ACTIONS</th>}
                </tr>
              </thead>
              <tbody>
                {slots.map((slot, index) => (
                  <tr key={slot.id}>
                    <td>
                      <span className="text-black font-w500">
                        FDRSLT{slot.id}
                      </span>
                    </td>
                    <td>{slot.label}</td>
                    <td>{formatTime(slot.from_time)}</td>
                    <td>{formatTime(slot.to_time)}</td>
                    <td>
                      {slot.max_orders > 0 ? (
                        <span
                          className="badge badge-info text-white"
                          style={{ borderRadius: 6 }}
                        >
                          Max {slot.max_orders}
                        </span>
                      ) : (
                        <span
                          className="badge badge-light"
                          style={{ borderRadius: 6, color: "#666" }}
                        >
                          Unlimited
                        </span>
                      )}
                    </td>
                    <td>
                      <ToggleButton
                        checked={slot.is_active}
                        disabled={!canManage}
                        onToggle={() => handleToggleActive(slot)}
                      />
                    </td>
                    {canManage && (
                      <td>
                        <div className="d-flex">
                          <button
                            type="button"
                            className="btn btn-primary shadow btn-xs sharp mr-2"
                            onClick={() => openEditModal(slot)}
                          >
                            <i className="fa fa-pencil" />
                          </button>
                          <button
                            type="button"
                            className="btn btn-danger shadow btn-xs sharp"
                            onClick={() => confirmDelete(slot)}
                          >
                            <i className="fa fa-trash" />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add / Edit React State Controlled Modal */}
      {isModalOpen && (
        <div
          className="modal fade show"
          style={{
            display: "block",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 1050,
          }}
        >
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div
              className="modal-content"
              style={{
                borderRadius: "15px",
                border: "none",
                boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
              }}
            >
              <div
                className="modal-header"
                style={{ borderBottom: "1px solid #f0f0f0", padding: "20px" }}
              >
                <h5
                  className="modal-title text-black"
                  style={{ fontWeight: "600", fontSize: "18px" }}
                >
                  {editMode ? "Edit Delivery Slot" : "Add Delivery Slot"}
                </h5>
                <button
                  type="button"
                  className="close"
                  style={{ fontSize: "24px", color: "#888" }}
                  onClick={() => setIsModalOpen(false)}
                >
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body" style={{ padding: "20px" }}>
                <form onSubmit={handleSubmit}>
                  <div className="form-group mb-3">
                    <label
                      className="text-black"
                      style={{ fontWeight: "500", marginBottom: "8px" }}
                    >
                      Slot Label
                    </label>
                    <input
                      type="text"
                      className={`form-control ${
                        errors.label ? "is-invalid" : ""
                      }`}
                      placeholder="e.g. Breakfast Slot"
                      value={formData.label}
                      onChange={(e) =>
                        setFormData({ ...formData, label: e.target.value })
                      }
                      style={{ borderRadius: "8px", padding: "10px 15px" }}
                    />
                    {errors.label && (
                      <div className="invalid-feedback">{errors.label}</div>
                    )}
                  </div>

                  <div className="row">
                    <div className="col-sm-6 form-group mb-3">
                      <label
                        className="text-black"
                        style={{ fontWeight: "500", marginBottom: "8px" }}
                      >
                        Start Time
                      </label>
                      <input
                        type="time"
                        step="1"
                        className={`form-control ${
                          errors.from_time ? "is-invalid" : ""
                        }`}
                        value={formData.from_time}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            from_time: e.target.value,
                          })
                        }
                        style={{ borderRadius: "8px", padding: "10px 15px" }}
                      />
                      {errors.from_time && (
                        <div className="invalid-feedback">
                          {errors.from_time}
                        </div>
                      )}
                    </div>
                    <div className="col-sm-6 form-group mb-3">
                      <label
                        className="text-black"
                        style={{ fontWeight: "500", marginBottom: "8px" }}
                      >
                        End Time
                      </label>
                      <input
                        type="time"
                        step="1"
                        className={`form-control ${
                          errors.to_time ? "is-invalid" : ""
                        }`}
                        value={formData.to_time}
                        onChange={(e) =>
                          setFormData({ ...formData, to_time: e.target.value })
                        }
                        style={{ borderRadius: "8px", padding: "10px 15px" }}
                      />
                      {errors.to_time && (
                        <div className="invalid-feedback">{errors.to_time}</div>
                      )}
                    </div>
                  </div>

                  <div className="form-group mb-3">
                    <label
                      className="text-black"
                      style={{ fontWeight: "500", marginBottom: "8px" }}
                    >
                      Max Orders (Capacity)
                    </label>
                    <input
                      type="number"
                      min="0"
                      className="form-control"
                      placeholder="0 for unlimited"
                      value={formData.max_orders}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          max_orders: e.target.value,
                        })
                      }
                      style={{ borderRadius: "8px", padding: "10px 15px" }}
                    />
                    <small className="form-text text-muted">
                      Set to 0 for unlimited bookings.
                    </small>
                  </div>

                  <div
                    className="form-group mb-4 d-flex align-items-center justify-content-between"
                    style={{
                      background: "#fcfcfc",
                      padding: "12px 15px",
                      borderRadius: "10px",
                      border: "1px solid #f0f0f0",
                    }}
                  >
                    <span className="text-black" style={{ fontWeight: "500" }}>
                      Active Status
                    </span>
                    <ToggleButton
                      checked={formData.is_active}
                      onToggle={(val) =>
                        setFormData({ ...formData, is_active: val })
                      }
                    />
                  </div>

                  <div
                    className="text-right"
                    style={{
                      borderTop: "1px solid #f0f0f0",
                      paddingTop: "15px",
                    }}
                  >
                    <button
                      type="button"
                      className="btn btn-light"
                      onClick={() => setIsModalOpen(false)}
                      style={{
                        borderRadius: "8px",
                        marginRight: "10px",
                        padding: "8px 20px",
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      style={{ borderRadius: "8px", padding: "8px 20px" }}
                    >
                      {editMode ? "Save Changes" : "Create Slot"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RestaurantDeliverySlots;
